import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { FileText, Shield, LogOut, CheckCircle } from 'lucide-react'
import { Modal, Button } from '../ui'
import { useAuthStore } from '../../stores/authStore'

// Current versions of terms and privacy policy
export const CURRENT_TERMS_VERSION = '1.0'
export const CURRENT_PRIVACY_VERSION = '1.0'

interface TermsAcceptanceModalProps {
  isOpen: boolean
  onAccept: () => void
  onDecline: () => void
}

const termsSchema = z.object({
  acceptTerms: z.boolean().refine(val => val === true, {
    message: 'Debes aceptar los términos y condiciones para continuar'
  }),
  acceptPrivacy: z.boolean().refine(val => val === true, {
    message: 'Debes aceptar la política de privacidad para continuar'
  })
})

type TermsFormData = z.infer<typeof termsSchema>

const TermsAcceptanceModal = ({ isOpen, onAccept, onDecline }: TermsAcceptanceModalProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const { user, signOut } = useAuthStore()

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch
  } = useForm<TermsFormData>({
    resolver: zodResolver(termsSchema),
    defaultValues: {
      acceptTerms: false,
      acceptPrivacy: false
    }
  })

  const watchedValues = watch()
  const canSubmit = watchedValues.acceptTerms && watchedValues.acceptPrivacy

  const onSubmit = async (data: TermsFormData) => {
    if (!user) return

    setIsLoading(true)
    try {
      // Save acceptance to user profile
      const acceptanceData = {
        termsAcceptedAt: new Date(),
        termsVersion: CURRENT_TERMS_VERSION,
        privacyAcceptedAt: new Date(),
        privacyVersion: CURRENT_PRIVACY_VERSION
      }

      // Update user via AuthStore
      await useAuthStore.getState().updateUserTermsAcceptance(acceptanceData)

      onAccept()
    } catch (error) {
      console.error('Error saving terms acceptance:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleDecline = async () => {
    try {
      await signOut()
      onDecline()
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {}} // No close function - must decide
      size="lg"
      showCloseButton={false}
      closeOnBackdropClick={false}
      closeOnEscape={false}
      title="Términos y Condiciones"
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Bienvenido a WildDraw
          </h2>
          <p className="text-gray-600">
            Para usar nuestra plataforma, necesitas aceptar nuestros términos y condiciones.
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Terms Summary */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
            <h3 className="font-semibold text-blue-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Resumen de términos importantes
            </h3>

            <div className="space-y-3 text-sm text-blue-800">
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Los sorteos se basan en los resultados oficiales de la ONCE</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Los premios se envían a la dirección que proporciones</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Debes ser mayor de 18 años para participar</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Tus datos personales están protegidos según el GDPR</span>
              </div>
            </div>
          </div>

          {/* Checkboxes */}
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
              <input
                {...register('acceptTerms')}
                id="accept-terms"
                type="checkbox"
                className="mt-1 h-5 w-5 sm:h-4 sm:w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="accept-terms" className="text-sm sm:text-sm text-gray-700 leading-5 cursor-pointer">
                He leído y acepto los{' '}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700 underline"
                >
                  Términos y Condiciones
                </a>
                {' '}de WildDraw
              </label>
            </div>
            {errors.acceptTerms && (
              <p className="text-sm text-red-600 ml-7">{errors.acceptTerms.message}</p>
            )}

            <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
              <input
                {...register('acceptPrivacy')}
                id="accept-privacy"
                type="checkbox"
                className="mt-1 h-5 w-5 sm:h-4 sm:w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="accept-privacy" className="text-sm sm:text-sm text-gray-700 leading-5 cursor-pointer">
                He leído y acepto la{' '}
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700 underline"
                >
                  Política de Privacidad
                </a>
                {' '}de WildDraw
              </label>
            </div>
            {errors.acceptPrivacy && (
              <p className="text-sm text-red-600 ml-7">{errors.acceptPrivacy.message}</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 pt-6">
            <Button
              type="button"
              variant="ghost"
              onClick={handleDecline}
              className="order-2 sm:order-1"
              disabled={isLoading}
              leftIcon={<LogOut className="w-4 h-4" />}
            >
              Rechazar y salir
            </Button>
            <Button
              type="submit"
              className="order-1 sm:order-2 flex-1"
              isLoading={isLoading}
              disabled={!canSubmit}
              leftIcon={<Shield className="w-4 h-4" />}
            >
              Aceptar y continuar
            </Button>
          </div>
        </form>

        <div className="text-xs text-gray-500 text-center">
          Al aceptar, confirmas que tienes al menos 18 años y que entiendes que participas en sorteos de forma voluntaria.
        </div>
      </div>
    </Modal>
  )
}

export default TermsAcceptanceModal